import * as React from 'react';
import DriveItemList from '../DriveItemList';
import { DriveListState, FetchStatus, getDriveItemStatusFromMap } from '../DriveItemList/reducer';
import { DownloadManagerState } from '../DownloadManager/reducer';
import Theater from '../Theater';
import { ListPageWrapper, NoItemsContainer } from './styles';
import { DriveItemListOrigin, NavigationItemData } from './constants';
import { Api } from '../../typings/api';
import { Translate } from 'react-redux-i18n';
import RoutesUtils from '../../utils/routes';
import Page from '../Page';
import NavigationListItem from '../../components/NavigationListItem';
import { ListDisplayType } from '../DriveItemList/constants';

interface AbstractDriveItemListPageParams {
	origin: DriveItemListOrigin;
	originParam?: string;
	driveItemId?: string;
}

interface AbstractDriveItemListPageProps {
	params: AbstractDriveItemListPageParams;
	galleryId: number;
	driveList: DriveListState;
	downloadManager: DownloadManagerState;
	displayType: ListDisplayType;
}

interface AbstractDriveItemListPageState {
	navigationItems: NavigationItemData[];
}

abstract class AbstractDriveItemListPage<P extends AbstractDriveItemListPageProps = AbstractDriveItemListPageProps>
	extends React.Component<P, AbstractDriveItemListPageState> {

	abstract headerLabel: string;
	abstract noItemsLabel: string;
	hasShareButton: boolean = false;
	hasDownloadButton: boolean = false;
	hasBottomBar: boolean = false;
	showUnreadBadge: boolean = false;

	constructor(props: P) {
		super(props);
		this.state = {
			navigationItems: [],
		};
	}

	componentDidMount() {
		this.initializeView(this.props);
	}

	componentWillReceiveProps(nextProps: P) {
		if (this.props.params !== nextProps.params) {
			this.initializeView(nextProps);
		}
	}

	renderNavigationItems() {
		const { driveList: { isEditMode }, displayType } = this.props;
		const { navigationItems } = this.state;

		return navigationItems.map(item => (
			<NavigationListItem
				key={`navItem-${item.label}`}
				itemLabel={`messages.${item.label}`}
				type={item.type}
				navigationPath={
					item.type === Api.DriveItemType.Directory
						? RoutesUtils.getPathDriveItemListPage(item.origin, null)
						: undefined
				}
				url={
					item.type === Api.DriveItemType.ExternalLink
						? item.url
						: undefined
				}
				image={item.image}
				displayType={displayType}
				isEditMode={isEditMode}
			/>
		));
	}

	render() {
		const { params } = this.props;
		const { navigationItems } = this.state;
		const { driveItemId, origin } = params;
		const driveFolderId = this.getDriveFolderId();
		const driveItemIds = this.getDriveItemIds();
		const resolvedDriveItemIds = this.getResolvedDriveItemIds(driveItemIds);
		const theaterDriveItemIds = this.getTheaterDriveItemIds(resolvedDriveItemIds);
		const isFetching = this.isFetching(resolvedDriveItemIds);

		this.leavePage = this.leavePage.bind(this);

		const driveItemsStore = this.props.driveList.driveItems;
		const driveItems = resolvedDriveItemIds.map(id => driveItemsStore[id]);
		const filteredDriveItems = driveItems.filter(item => item !== undefined);
		const hasNoItems = !isFetching && resolvedDriveItemIds.length < 1 && navigationItems.length < 1;

		return (
			<>
				<Page
					driveItems={filteredDriveItems}
					hasBottomBar={this.hasBottomBar}
					isScrollable={false}
				>
					<ListPageWrapper>
						<DriveItemList
							hasBottomBar={this.hasBottomBar}
							params={params}
							driveItemIdsToDisplay={resolvedDriveItemIds}
							folderId={driveFolderId}
							isFetching={isFetching}
							showUnreadBadge={this.showUnreadBadge}
							title={this.headerLabel}
							navigationItems={this.renderNavigationItems()}
							isFilesDndEnabled={true}
							shouldDisplayFilesToast={true}
						/>
						{hasNoItems &&
							<NoItemsContainer>
								<Translate value={this.noItemsLabel} />
							</NoItemsContainer>
						}
					</ListPageWrapper>
				</Page>
				{driveItemId &&
					<Theater
						origin={origin}
						sliderKey={this.sliderKey}
						driveItemId={parseInt(driveItemId, 10)}
						driveItemIds={theaterDriveItemIds}
						isLoading={isFetching}
						leavePage={this.leavePage}
						folderId={driveFolderId}
					/>
				}
			</>
		);
	}

	isFetching(driveItemIds: number[]): boolean {
		const { driveItemsFetchStatus, driveItems } = this.props.driveList;

		for (let i = 0; i < driveItemIds.length; i++) {
			const driveItemId = driveItemIds[i];
			const driveItemFetchStatus = getDriveItemStatusFromMap(driveItemsFetchStatus, driveItemId);
			const { metaFetchStatus } = driveItemFetchStatus;

			if ((!driveItems[driveItemId] && metaFetchStatus !== FetchStatus.ERROR) || metaFetchStatus === FetchStatus.FETCHING) {
				return true;
			}
		}
		return false;
	}

	getTheaterDriveItemIds(driveItemIds: number[]): number[] {
		const driveFolderId = this.getDriveFolderId();
		const driveItemsStore = this.props.driveList.driveItems;
		let filteredDriveItemIds: number[] = [];

		if (driveFolderId) {
			const driveFolder = driveItemsStore[driveFolderId];
			if (driveFolder) {
				const { resources } = driveFolder;
				return resources ? resources.filter(resource => resource.type !== Api.DriveItemType.Directory).map(resource => resource.id) : [];
			}
		} else {
			for (let i = 0; i < driveItemIds.length; i++) {
				const driveItemId = driveItemIds[i];
				const driveItem = driveItemsStore[driveItemId];
				if (driveItem) {
					if (driveItem.type !== Api.DriveItemType.Directory) {
						filteredDriveItemIds.push(driveItemId);
					}
				}
			}
		}

		return filteredDriveItemIds;
	}

	// Filter the list and remove the one that have fetch error (forbidden, expired, deleted, ...)
	getResolvedDriveItemIds(driveItemIds: number[]): number[] {
		const { driveItemsFetchStatus } = this.props.driveList;

		return driveItemIds.filter(driveItemId => {
			const driveItemFetchStatus = getDriveItemStatusFromMap(driveItemsFetchStatus, driveItemId);
			const { metaFetchStatus } = driveItemFetchStatus;
			return metaFetchStatus !== FetchStatus.ERROR;
		});
	}

	protected getDriveFolderId(props: P = this.props): number | undefined {
		const { galleryId, params } = props;
		const { origin, originParam } = params;

		if (origin === DriveItemListOrigin.Folder) {
			return originParam ? parseInt(originParam, 10) : galleryId;
		}
		return;
	}

	leavePage(): void {
		const { galleryId, params } = this.props;
		let { originParam } = params;
		const origin = this.props.params.origin as DriveItemListOrigin;

		if (origin === DriveItemListOrigin.Folder && originParam === galleryId.toString()) {
			originParam = '';
		}

		window.location.hash = `${RoutesUtils.getPathDriveItemListPage(origin, originParam || null)}`;
	}

	get sliderKey(): string {
		const { origin, originParam } = this.props.params;
		return `${origin}${originParam ? '-' + originParam : ''}`;
	}

	abstract initializeView(props: P): void;

	abstract getDriveItemIds(): number[];
}

export {
	AbstractDriveItemListPageParams,
	AbstractDriveItemListPageProps,
};
export default AbstractDriveItemListPage;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemListPage/abstract.tsx